import {
  Warband,
  CreateWarbandRequest,
  BaseResource,
  WarbandSpellsXr,
  WarbandLevelUp,
} from '../types';
import {
  getAllWarbands,
  loadWarbandData,
  saveWarbandData,
  deleteWarband as deleteWarbandData,
  createWarband as createWarbandData,
} from './localStorageService';

export const warbandService = {
  getAll: () => {
    const warbands = getAllWarbands();
    return Promise.resolve({ data: warbands as Warband[] });
  },

  getById: (id: string) => {
    const warbands = getAllWarbands();
    const warband = warbands.find((w) => w.id === id) ?? null;
    return Promise.resolve({ data: warband as Warband | null });
  },

  create: (data: CreateWarbandRequest) => {
    const stored = createWarbandData(data);
    void import('./syncCoordinator').then(({ queueSyncEntity }) => {
      queueSyncEntity(`warband:${stored.warband.id}`);
    });
    return Promise.resolve({ data: stored.warband as Warband });
  },

  update: (id: string, data: Partial<Warband>) => {
    const existing = loadWarbandData(id);
    if (!existing) return Promise.resolve({ data: null as Warband | null });
    const updated = { ...existing, warband: { ...existing.warband, ...data } };
    saveWarbandData(id, updated);
    void import('./syncCoordinator').then(({ queueSyncEntity }) => {
      queueSyncEntity(`warband:${id}`);
    });
    return Promise.resolve({ data: updated.warband as Warband });
  },

  delete: (id: string) => {
    deleteWarbandData(id);
    void import('./syncCoordinator').then(({ queueSyncEntity }) => {
      queueSyncEntity(`warband:${id}`);
    });
    return Promise.resolve({ data: undefined });
  },

  getBaseResources: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const ids = data?.selectedBaseResourceIds ?? [];
    // Hydrate base resources from reference data
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
    const refData = useReferenceDataStore.getState();
    const resources = ids
      .map((id) => refData.allBaseResources.find((r) => r.id === id))
      .filter((r): r is BaseResource => r !== null && r !== undefined);
    return Promise.resolve({ data: resources });
  },

  getSpells: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const spellXrs: WarbandSpellsXr[] = (data?.selectedSpellIds ?? []).map((id) => ({
      warbandId,
      warbandSpellId: id,
      spellId: id,
    }));
    return Promise.resolve({ data: spellXrs });
  },

  getSpell: (warbandId: string, spellId: string) => {
    const data = loadWarbandData(warbandId);
    const found = data?.selectedSpellIds.includes(spellId) ?? false;
    const xr: WarbandSpellsXr | null = found ? { warbandId, warbandSpellId: spellId, spellId } : null;
    return Promise.resolve({ data: xr });
  },

  addSpell: (warbandId: string, spellId: string) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null });
    if (!data.selectedSpellIds.includes(spellId)) {
      data.selectedSpellIds = [...data.selectedSpellIds, spellId];
      saveWarbandData(warbandId, data);
    }
    const xr: WarbandSpellsXr = { warbandId, warbandSpellId: spellId, spellId };
    return Promise.resolve({ data: xr });
  },

  deleteSpell: (warbandId: string, spellId: string) => {
    const data = loadWarbandData(warbandId);
    if (data) {
      data.selectedSpellIds = data.selectedSpellIds.filter((id) => id !== spellId);
      saveWarbandData(warbandId, data);
    }
    return Promise.resolve({ data: undefined });
  },

  addSpellsBulk: (warbandId: string, spellIds: string[]) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: undefined });
    const existingSet = new Set(data.selectedSpellIds);
    for (const id of spellIds) existingSet.add(id);
    data.selectedSpellIds = Array.from(existingSet);
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: undefined });
  },

  getLevelUps: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    return Promise.resolve({ data: (data?.levelUps ?? []) as WarbandLevelUp[] });
  },
};
